/**
 * The editable deal fields, in a founder's words, and the difference between two versions.
 *
 * What is **not** here is the point: nothing in this module decides which fields exist, which
 * of them are material, or what a material edit does. Core publishes all three in
 * `editPolicy` (canon CORE-015, `contracts/API.md` § `editPolicy`), and a second copy in a
 * UI would drift the first time a canon decision changed the list — leaving the editor
 * warning a founder about the wrong consequence. This module only knows how to *name* a
 * field on screen and how to tell two values apart.
 */
import type { EditableDealField } from "../api/types";

/**
 * The label each control carries, and the name used when a change is described in a
 * sentence. They are deliberately the same word: a founder told "the deal link will send
 * this back to review" must be able to find the box that says "Deal link".
 */
const LABELS: Record<EditableDealField, string> = {
  productName: "Product name",
  productUrl: "Product website",
  logoUrl: "Logo image URL",
  shortDescription: "Short description",
  dealUrl: "Deal link",
  dealType: "Deal type",
  originalPriceCents: "Usual price",
  dealPriceCents: "Deal price",
  valueNotes: "What the deal is worth",
  couponCode: "Coupon code",
  limitations: "Limitations",
  isExclusive: "Exclusive to Dealbid",
};

export function labelForField(field: EditableDealField): string {
  return LABELS[field];
}

/**
 * Core's field name to a label, for a name this app does not recognise.
 *
 * A `VALIDATION_FAILED` can name a field this bundle draws no control for — core is the
 * newer half of the contract — and the message must still reach the founder. Returning the
 * raw name is honest: it is what core called it.
 */
export function labelForFieldName(field: string): string {
  return Object.hasOwn(LABELS, field) ? LABELS[field as EditableDealField] : field;
}

/**
 * The field names in one list, joined into a phrase.
 *
 * Used in the sentence that says which pending changes will send a deal back to review, so
 * the founder reads "the deal link and the deal price" rather than a comma-separated dump.
 */
export function joinFieldLabels(fields: readonly EditableDealField[]): string {
  const labels = fields.map((field) => labelForField(field).toLocaleLowerCase());
  if (labels.length === 0) return "";
  if (labels.length === 1) return labels[0] ?? "";
  const last = labels[labels.length - 1] ?? "";
  return `${labels.slice(0, -1).join(", ")} and ${last}`;
}
